/**
 * Free Shipping Bar — domain-based fixed display currency.
 *
 * Loaded UNCONDITIONALLY in both layout/theme.liquid and layout/theme.gem-layout-none.liquid,
 * after jquery.js and after the ajaxify-shop.js conditional block.
 *
 * DOMAIN-BASED CONVERSION (replaces the Phase 2 Bucks Currency Converter adapter): this store
 * does not use Shopify Payments, so checkout is ALWAYS in USD regardless of domain or any
 * currency picker on the page. Display currency is fixed PER DOMAIN, not per shopper
 * selection or third-party app state:
 *   - uk.vitamingrocer.com  -> GBP, £25 threshold
 *   - everything else (vitamingrocer.com, .myshopify.com preview, etc.) -> USD, $50 threshold
 * Two independent, unrelated currency-display systems exist on this site (Bucks Currency
 * Converter's own widget, and this theme's much older jquery.currencies.min.js cosmetic
 * price-display switcher) — NEITHER reflects or changes the real checkout currency, and
 * neither is read here anymore. On the GBP domain, the real cart total (cart.total_price,
 * always USD cents) is converted into GBP using a single merchant-editable exchange rate
 * (settings.fsb_usd_to_gbp_rate, emitted as data-usd-to-gbp-rate) — intentionally NOT the
 * real market FX rate; a cart can cross the £25 threshold without reaching $50 at actual USD
 * checkout, by design. On the USD domain, no conversion is needed — the cart total already
 * IS the display total.
 *
 * Because this no longer depends on any third-party app's async global, the bar renders
 * immediately on page ready — no readiness polling needed.
 *
 * APP-RESILIENT CART TRACKING (carried forward from the 02-03 gap-closure fix): other store
 * apps reassign Shopify.onCartUpdate after this script wraps it, which orphaned the bar in
 * earlier testing. We do not rely on owning Shopify.onCartUpdate. Instead we bind a delegated
 * jQuery ajaxComplete listener on cart-MUTATING endpoints (/cart/add|change|update|clear) and
 * re-read the authoritative /cart.js, so the bar re-renders on EVERY cart change no matter
 * which app currently owns the cart handler. Shopify.onCartUpdate is still wrapped as a
 * secondary path, but never depended on for staying the owner.
 *
 * Self-correcting render: render() fully rewrites the message + --unlocked state every call
 * (no early-return that leaves a stale class), and visibility is governed by the SINGLE
 * --pending class (no inline $bar.show()/hide()).
 *
 * Reads the per-currency threshold + money-format JSON maps from the snippet's
 * data-thresholds / data-money-formats attributes (single source of truth, D-07) — the bar
 * looks up whichever currency key the current domain resolves to.
 */
(function ($) {
  'use strict';

if (typeof Shopify === 'undefined') { window.Shopify = {}; }
if (typeof Shopify.formatMoney !== 'function') {
  Shopify.formatMoney = function (cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    var patt = /\{\{\s*(\w+)\s*\}\}/;
    var match = patt.exec(format);
    if (!match) { return format; }
    function fmt(n, precision, thousands, decimal) {
      thousands = thousands || ','; decimal = decimal || '.';
      n = (n / 100).toFixed(precision === undefined ? 2 : precision);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[0] + (parts[1] !== undefined ? decimal + parts[1] : '');
    }
    var value;
    switch (match[1]) {
      case 'amount':                              value = fmt(cents, 2);       break;
      case 'amount_no_decimals':                  value = fmt(cents, 0);       break;
      case 'amount_with_comma_separator':         value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      default:                                    value = fmt(cents, 2);
    }
    return format.replace(patt, value);
  };
}

  var DISMISS_KEY = 'fsBarDismissed';
  // Cart total (cents) captured at the moment of dismiss. We compare the current total
  // against this; if it changed, the cart changed while dismissed, so the bar reappears
  // per BAR-03.
  var DISMISS_TOTAL_KEY = 'fsBarDismissedAtCents';
  var $bar;
  var moneyFormat = '${{amount}}';

  // Checkout is always USD (no Shopify Payments) regardless of domain. See the file header
  // for why display currency is fixed per domain rather than read from Bucks or the legacy
  // switcher.
  var GBP_DOMAIN = 'uk.vitamingrocer.com';
  var EXPECTED_CART_CURRENCY = 'USD';

  // Resolved once on load from the current hostname (matches the snippet's request.host
  // check) — not expected to change within a page view.
  var displayCurrency = 'USD';

  // Latest known cart total + its currency. Seeded from the snippet's server-rendered data
  // attributes on load, then refreshed from /cart.js on every cart mutation.
  var lastCents = 0;
  var lastCartCurrency = null; // read dynamically; guarded against in render() (fail-safe).

  // Merchant-set USD->GBP rate (settings.fsb_usd_to_gbp_rate), seeded once on load. This is
  // a theme setting, not expected to change within a session, so it is read once rather than
  // re-fetched on every cart update. Only used when displayCurrency is GBP.
  var usdToGbpRate = 0;

  function resolveDisplayCurrencyForDomain() {
    return window.location.hostname === GBP_DOMAIN ? 'GBP' : 'USD';
  }

  // --- Dismiss state (sessionStorage) ------------------------------------------------------

  function isDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      // private browsing / storage disabled — fail open (show bar)
      return false;
    }
  }

  function setDismissed(val) {
    try {
      sessionStorage.setItem(DISMISS_KEY, val ? '1' : '0');
    } catch (e) {
      // no-op if storage unavailable
    }
  }

  function getDismissedAtCents() {
    try {
      var raw = sessionStorage.getItem(DISMISS_TOTAL_KEY);
      return raw === null ? null : parseInt(raw, 10);
    } catch (e) {
      return null;
    }
  }

  function setDismissedAtCents(cents) {
    try {
      sessionStorage.setItem(DISMISS_TOTAL_KEY, String(cents));
    } catch (e) {
      // no-op if storage unavailable
    }
  }

  // --- Money format ------------------------------------------------------------------------

  function getMoneyFormat() {
    // Prefer the theme-wide app.data.money_format (always present, ungated — set in
    // theme.liquid's inline <script> block) for consistency with the rest of the theme.
    if (window.app && window.app.data && window.app.data.money_format) {
      return window.app.data.money_format;
    }
    // Fallback to the snippet's own data-money-format attribute (JSON-encoded string).
    if ($bar && $bar.length) {
      var raw = $bar.attr('data-money-format');
      if (raw) {
        try {
          return JSON.parse(raw);
        } catch (e) {
          return raw;
        }
      }
    }
    return moneyFormat;
  }

  // --- Domain-based conversion ---------------------------------------------------------------

  // Converts the real (always-USD) cart total into the resolved display currency. Identity
  // on the USD domain (no conversion needed); rate-based on the GBP domain. Returns null
  // (fail-safe, caller keeps the bar hidden) if the cart currency isn't the expected USD, the
  // target currency is unsupported, or the GBP rate is missing/zero/invalid — never guesses
  // (T-02-06).
  function convertToDisplay(cents) {
    if (lastCartCurrency !== EXPECTED_CART_CURRENCY) {
      return null;
    }
    if (displayCurrency === EXPECTED_CART_CURRENCY) {
      return cents || 0;
    }
    if (displayCurrency === 'GBP') {
      if (!usdToGbpRate || isNaN(usdToGbpRate)) {
        return null;
      }
      return Math.round((cents || 0) * usdToGbpRate);
    }
    return null;
  }

  function getThresholdsMap() {
    if (!$bar || !$bar.length) {
      return {};
    }
    var raw = $bar.attr('data-thresholds');
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  function getThresholdFor(code) {
    var map = getThresholdsMap();
    if (code && map && map.hasOwnProperty(code)) {
      return map[code];
    }
    // Not a configured currency — D-04 hide signal.
    return null;
  }

  function getMoneyFormatsMap() {
    if (!$bar || !$bar.length) {
      return {};
    }
    var raw = $bar.attr('data-money-formats');
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  function stripHtml(str) {
    // Some third-party format strings carry an HTML <span> wrapper. The bar renders via
    // .text() (XSS-safe), so strip any tags before use rather than let literal markup show.
    return String(str).replace(/<[^>]*>/g, '');
  }

  function formatMoneyForCurrency(cents, code) {
    var formats = getMoneyFormatsMap();
    var format =
      code && formats && formats.hasOwnProperty(code) ? formats[code] : getMoneyFormat();
    return Shopify.formatMoney(cents, stripHtml(format));
  }

  // --- Merchant-editable copy (Phase 3) -----------------------------------------------------

  var DEFAULT_COPY = {
    empty: 'All orders over [amount] ship for free!',
    below: 'Spend another [amount] to earn free shipping!',
    unlocked: 'Congratulations 🎉 You’ve earned free shipping!'
  };

  function getCopyTemplate(state) {
    var raw = null;
    if ($bar && $bar.length) {
      if (state === 'empty') {
        raw = $bar.attr('data-copy-empty');
      } else if (state === 'below') {
        raw = $bar.attr('data-copy-below');
      } else {
        raw = $bar.attr('data-copy-unlocked');
      }
    }
    return raw || DEFAULT_COPY[state];
  }

  // Splits copyTemplate on the literal "[amount]" token and writes prefix/amount/suffix into
  // the three pre-placed spans via .text() only — no markup parsing anywhere (D3-07/T-02-03).
  // Graceful degrade: no token, or no amountText, puts the whole template in prefix and
  // clears amount+suffix (D3-06/Specifics).
  function renderMessage($message, copyTemplate, amountText) {
    var $prefix = $message.find('.free-shipping-bar__message-prefix');
    var $amount = $message.find('.free-shipping-bar__amount');
    var $suffix = $message.find('.free-shipping-bar__message-suffix');
    var token = '[amount]';
    var idx = amountText ? copyTemplate.indexOf(token) : -1;

    if (idx === -1) {
      $prefix.text(copyTemplate);
      $amount.text('');
      $suffix.text('');
      return;
    }

    $prefix.text(copyTemplate.slice(0, idx));
    $amount.text(amountText);
    $suffix.text(copyTemplate.slice(idx + token.length));
  }

  // --- Visibility (single mechanism: the --pending class) ----------------------------------

  // Body class kept in lockstep with the bar's own visibility so the fixed bar pushes page
  // content down by its height while showing, with no gap when hidden/dismissed.
  function hideBar() {
    if ($bar && $bar.length) {
      $bar.addClass('free-shipping-bar--pending');
    }
    $('body').removeClass('has-free-shipping-bar');
  }

  function showBar() {
    if ($bar && $bar.length) {
      $bar.removeClass('free-shipping-bar--pending');
    }
    $('body').addClass('has-free-shipping-bar');
  }

  // --- Single, self-correcting render ------------------------------------------------------

  // Fully rewrites the message + --unlocked + visibility every call. Idempotent: calling it
  // repeatedly with the same cart yields the same result and never leaves a stale celebration
  // or threshold behind.
  function render() {
    if (!$bar || !$bar.length) {
      return;
    }

    var display = displayCurrency;
    var threshold = getThresholdFor(display);
    if (threshold === null || threshold === undefined) {
      // This domain's currency isn't enabled in theme settings — stay hidden (D-04).
      hideBar();
      return;
    }

    var displayedCents = convertToDisplay(lastCents);
    if (displayedCents === null) {
      // Unexpected cart currency, or missing/invalid rate — fail safe, stay hidden rather
      // than show a wrong number (T-02-06).
      hideBar();
      return;
    }

    var remaining = threshold - (displayedCents || 0);
    if (remaining < 0) {
      remaining = 0;
    }

    var $message = $bar.find('.free-shipping-bar__message');

    // Three-state branch (D3-08), mirroring the snippet's server-render order exactly so
    // first paint and the first live AJAX update never disagree (Pitfall 6):
    //   empty (cart total is 0)  -> below (remaining > 0)  -> unlocked (else).
    if (displayedCents === 0) {
      renderMessage($message, getCopyTemplate('empty'), formatMoneyForCurrency(threshold, display));
      $bar.removeClass('free-shipping-bar--unlocked');
    } else if (remaining > 0) {
      renderMessage($message, getCopyTemplate('below'), formatMoneyForCurrency(remaining, display));
      $bar.removeClass('free-shipping-bar--unlocked');
    } else {
      renderMessage($message, getCopyTemplate('unlocked'), '');
      $bar.addClass('free-shipping-bar--unlocked');
    }

    // Reveal unless the shopper dismissed the bar for this exact cart state. A changed cart
    // total since dismiss means the cart changed — reappear per BAR-03.
    if (isDismissed() && getDismissedAtCents() === lastCents) {
      hideBar();
    } else {
      if (isDismissed()) {
        setDismissed(false);
      }
      showBar();
    }
  }

  // --- Authoritative cart re-read ----------------------------------------------------------

  // Reads /cart.js for the true total + currency (never trusts an app-mutated cart object),
  // then renders. /cart.js is intentionally NOT matched by the ajaxComplete mutation regex,
  // so this read does not retrigger itself.
  function refreshFromCart() {
    $.ajax({ url: '/cart.js', dataType: 'json', cache: false })
      .done(function (cart) {
        if (cart && typeof cart.total_price !== 'undefined') {
          lastCents = cart.total_price;
          lastCartCurrency = cart.currency || lastCartCurrency;
        }
        render();
      })
      .fail(function () {
        // Network hiccup — re-render with the last known total rather than going stale.
        render();
      });
  }

  // Public hook (back-compat + diagnostics). update() now forces an authoritative refresh.
  window.FreeShippingBar = {
    update: function () {
      refreshFromCart();
    },
    render: render
  };

  $(document).ready(function () {
    $bar = $('#free-shipping-bar');
    if (!$bar.length) {
      return;
    }

    lastCents = parseInt($bar.attr('data-current-cents'), 10) || 0;
    lastCartCurrency = $bar.attr('data-cart-currency') || null;
    usdToGbpRate = parseFloat($bar.attr('data-usd-to-gbp-rate')) || 0;
    displayCurrency = resolveDisplayCurrencyForDomain();

    // Honor a prior dismiss only while the cart is unchanged; a changed cart total since
    // dismiss (e.g. a cart-page Update full reload) reappears per BAR-03.
    if (isDismissed()) {
      var dismissedAt = getDismissedAtCents();
      if (dismissedAt !== null && lastCents !== dismissedAt) {
        setDismissed(false);
      }
    }

    $bar.find('.free-shipping-bar__dismiss').on('click', function () {
      setDismissed(true);
      setDismissedAtCents(lastCents);
      hideBar();
    });

    // PRIMARY cart tracking (app-resilient): re-render on ANY cart-MUTATING jQuery AJAX,
    // regardless of which app currently owns Shopify.onCartUpdate. This is the root-cause
    // fix for the orphaned-handler stale bar. We re-read /cart.js (NOT matched below) so we
    // never trust an app-mutated cart object.
    var cartMutationRe = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
    var debounce;
    $(document).ajaxComplete(function (event, xhr, settings) {
      var url = settings && settings.url;
      if (url && cartMutationRe.test(url)) {
        clearTimeout(debounce);
        // Let the triggering app's own handler finish first, then re-read the cart.
        debounce = setTimeout(refreshFromCart, 80);
      }
    });

    // SECONDARY path: compose with Shopify.onCartUpdate if it is ours at ready time. We do
    // NOT rely on staying the owner (other apps reassign it) — ajaxComplete above is the
    // reliable path. Guarded so a later app overwrite simply means this stops firing.
    if (typeof Shopify !== 'undefined') {
      var existingOnCartUpdate =
        typeof Shopify.onCartUpdate === 'function' ? Shopify.onCartUpdate : null;
      Shopify.onCartUpdate = function (cart, form) {
        if (existingOnCartUpdate) {
          existingOnCartUpdate(cart, form);
        }
        if (cart && typeof cart.total_price !== 'undefined') {
          lastCents = cart.total_price;
          lastCartCurrency = cart.currency || lastCartCurrency;
        }
        render();
      };
    }

    // No third-party app dependency for currency resolution anymore — render immediately
    // from the server-provided total (no-flash, D-05, but no async wait either).
    render();
  });
})(jQuery);
